import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../../hooks/useIsMobile";
import axions from "axios";
import { Trash2, SquarePen } from "lucide-react";
import AgGridActionMenu from "../../../components/AgGridAction/AgGridAction";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import TitleCard from "../../../components/TitleCard/TitleCard";
import Loader from "../../../components/Loader/Loader";
import ReusableModal from "../../../components/Modal/ReusableModal";
import DepartmenstForm from "./DepartmentsForm";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import MobileCardList from "../../../components/MobileCardList/MobileCardList";
import StatusBadge from "../../../components/StatusBadge/StatusBadge";
import ActionButton from "../../../components/ActionButton/ActionButton";


const DepartmentsList = () => {
    const navigate = useNavigate();
    const gridApiRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        type: "confirm",
        title: "Eliminar Despartamento",
        message: "¿Estás seguro de que deseas eliminar este Departamento?",
        onConfirm: null
    });


    const isMobile = useIsMobile();

    const components = useMemo(() => ({
        actionMenu: AgGridActionMenu,
    }), []);

    const [colDefs] = useState([
        { field: "description", headerName: "Departamento" },
        { field: "code", headerName: "Código" },
        { field: "active", headerName: "Estado", cellRenderer: (params) => (<StatusBadge active={params.value} />) },
        {
            headerName: "Acciones", field: "actions",
            cellRenderer: "actionMenu",
            cellRendererParams: {
                onEdit: (params) => handleEditDepartment(params),
                onDelete: (params) => handleDeleteDepartment(params),
            },
            sortable: false,
            filter: false,
            width: 150,
            cellStyle: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
        },
    ]);

    const defaultColDef = useMemo(() => ({
        flex: 1,
        filter: false,
        floatingFilter: false,
        editable: false,
        cellStyle: { textAlign: "normal" },
    }), []);

    const createDataSource = useCallback(() => {
        return {
            getRows: async (params) => {
                setLoading(true);
                try {
                    const size = params.endRow - params.startRow;
                    const page = size > 0 ? params.startRow / size : 0;

                    const token = localStorage.getItem("access_token");
                    const url = `${process.env.REACT_APP_API_URL}/department/get-all`;

                    const response = await axions.get(url, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: { page, size },
                    });

                    params.successCallback(response.data.data, response.data.total);
                } catch (error) {
                    console.error("Error cargando Departamentos:", error);
                    params.failCallback();
                } finally {
                    setLoading(false);
                }
            }
        };
    }, []);

    const onGridReady = useCallback((params) => {
        gridApiRef.current = params.api;

        if (typeof params.api.setDatasource === "function") {
            params.api.setDatasource(createDataSource());
        } else {
            params.api.setGridOption("datasource", createDataSource());
        }
    }, [createDataSource]);

    const onPaginationChanged = useCallback(() => {
        if (!gridApiRef.current) return;
        const api = gridApiRef.current;
        const newPageSize = api.paginationGetPageSize();
        setPageSize((prev) => (prev !== newPageSize ? newPageSize : prev));
    }, []);

    // ACCIONES
    const handleCreateDepartment = () => navigate("../masters/departments-create");


    const handleEditDepartment = (params) => {
        const data = params?.rowData || params;
        setSelectedDepartment(data);
        setShowEditModal(true);
    };

    const handleDeleteDepartment = (params) => {
        const data = params?.data || params;
        const id = data._id;
        if (!id) return;

        setConfirmModal({
            isOpen: true,
            type: "confirm",
            title: "Eliminar Departamento",
            message: "¿Estás seguro de que deseas eliminar este Departamento?",
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem("access_token");
                    await axions.delete(`${process.env.REACT_APP_API_URL}/department/delete/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                    );
                    setConfirmModal({
                        isOpen: true,
                        type: "success",
                        title: "Departamento eliminado",
                        message: "El departemento se eliminó correctamente.",
                        onConfirm: () => {
                            setConfirmModal({ ...confirmModal, isOpen: false });
                        }
                    });
                    if (gridApiRef.current?.purgeInfiniteCache) {
                        setLoading(true);
                        gridApiRef.current.purgeInfiniteCache();
                    }
                    // MOBILE
                    if (isMobile) {
                        setMobilePage(0); // Reset to first page after deletion
                        await loadMobileData(); //reload mobile data after deletion
                    }
                } catch (error) {
                    setConfirmModal({
                        isOpen: true,
                        type: "error",
                        title: "Error al eliminar departamento",
                        message: "Ocurrió un error al intentar eliminar el departamento.",
                        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                    });
                }
            },
        });
    };

    // MOBILE VERSION (cards con paginación al backend)
    const [mobileDepartment, setMobileDepartment] = useState([]);
    const [mobilePage, setMobilePage] = useState(0);
    const [mobileTotal, setMobileTotal] = useState(0)

    const loadMobileData = useCallback(async (page, size) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("access_token");
            const url = `${process.env.REACT_APP_API_URL}/department/get-all`;

            const response = await axions.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { page: mobilePage, size: pageSize },
            });

            setMobileDepartment(response.data.data);
            setMobileTotal(response.data.total);
        } catch (error) {
            console.error("Error cargando Departamentos:", error);
        } finally {
            setLoading(false);
        }
    }, [mobilePage, pageSize]);


    useEffect(() => {
        if (isMobile) loadMobileData();
    }, [isMobile, loadMobileData]);

    const totalMobilePages = Math.ceil(mobileTotal / pageSize);

    return (
        <>
            <TitleCard title="Departamentos" />

            {/**Boton crear */}
            <div className="flex justify-end my-1">
                <ActionButton
                    onClick={handleCreateDepartment}
                />
            </div>

            {/* DESKTOP: AG GRID */}
            {!isMobile && (
                <div
                    className="relative ag-theme-material my-4"
                    style={{ height: 530 }}
                >
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                            <Loader message="Cargando departamentos..." />
                        </div>
                    )}

                    <AgGridReact
                        rowModelType="infinite"
                        pagination={true}
                        paginationPageSize={pageSize}
                        paginationPageSizeSelector={[10, 25, 50]}
                        cacheBlockSize={pageSize}
                        maxBlocksInCache={5}
                        components={components}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        onGridReady={onGridReady}
                        onPaginationChanged={onPaginationChanged}
                    />
                </div>
            )}


            <MobileCardList
                isMobile={isMobile}
                loading={loading}
                loaderText="Cargando departamentos..."
                data={mobileDepartment}
                mobilePage={mobilePage}
                setMobilePage={setMobilePage}
                totalPages={totalMobilePages}
                totalItems={mobileTotal}
                pageSize={pageSize}
                renderItem={(department) => (
                    <div className="flex">
                        <div className="flex-1 grid gap-4 grid-cols-1 [@media(min-width:430px)]:grid-cols-2">
                            <h2 className="text-lg font-semibold col-span-full">
                                {department.description}
                            </h2>

                            <p className="text-sm text-gray-600">{department.code}</p>
                            <div className="flex items-center gap-2">
                                <StatusBadge active={department.active} />
                            </div>
                        </div>

                        <div className="flex flex-col justify-center items-end space-y-3">
                            <button onClick={() => handleEditDepartment(department)}>
                                <SquarePen size={18} color="#16A34A" strokeWidth={1.2} />
                            </button>

                            <button onClick={() => handleDeleteDepartment({ data: department })}>
                                <Trash2 size={18} color="#B91C1C" strokeWidth={1.2} />
                            </button>
                        </div>
                    </div>
                )}
            />

            {/* MODAL EDITAR */}
            <ReusableModal
                isOpen={showEditModal}
                title="Editar Departamento"
                onClose={() => setShowEditModal(false)}
            >
                <DepartmenstForm
                    initialData={selectedDepartment}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedDepartment(null);

                        setConfirmModal({
                            isOpen: true,
                            type: "success",
                            title: "Departamento actualizado",
                            message: "Los datos del departamento se guardaron correctamente.",
                            onConfirm: () =>
                                setConfirmModal({ ...confirmModal, isOpen: false }),
                        });

                        if (gridApiRef.current?.purgeInfiniteCache) {
                            setLoading(true);
                            gridApiRef.current.purgeInfiniteCache();
                        }
                        // MOBILE
                        if (isMobile) {
                            loadMobileData();
                        }
                    }}
                    onCancel={() => {
                        setShowEditModal(false);
                        setSelectedDepartment(null);
                    }}
                />
            </ReusableModal>
            {/* MODAL CONFIRMAR */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                type={confirmModal.type}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            />
        </>
    )

}


export default DepartmentsList;